import moment from "moment";
import qs from "qs";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router";
import { getBuildingsAsync } from "src/api/building/building-api";
import { CommonFacilityType } from "src/api/building/building-types";
import { getContractApply } from "src/api/contract/contract-api";
import { useApiOperation } from "src/api/hooks";
import { getNoticeListAsync } from "src/api/notice/notice-api";
import { NoticeListParams, NoticeModel } from "src/api/notice/notice-types";
import { Order, PageMeta, Sort } from "src/api/public-types";
import { BaseButton, BaseInputWithSearch, BaseModal, BaseSelect, BaseTable } from "src/components";
import RangeDatepicker from "src/components/RangeDatepicker";
import useNavigate from "src/hooks/usePartnerNavigate";
import { PagePath } from "src/pages/product/details";
import { qsParse, YmdFormat } from "src/utils";
import { Modal, NoticeCategory } from "../notice-types";
import noticeColumns from "./columns/noticeColumns";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import SearchResetButton from "src/components/SearchResetButton";

// 검색어 옵션 (value 가 곧 API 요청 파라미터명. 전체는 search001)
const searchTypes = [
  { label: "전체", value: "" },
  { label: "공지사항 ID", value: "id" },
  { label: "제목", value: "containsTitle" },
];

// 검색 옵션이 바뀌어도 URL 에 남는 이전 옵션의 검색 파라미터 (요청 전 제거 대상)
const searchParamKeys = ["id", "containsTitle", "search001"] as const;

// selectOption :: 공개여부
const isDisplayed = [
  { value: "", label: "전체" },
  { value: "true", label: "공개" },
  { value: "false", label: "비공개" },
];

// selectOption :: 카테고리
const categoryOptions = [
  { value: "", label: "전체" },
  { value: NoticeCategory.FACILITY_MAINTENANCE, label: "점검/보수" },
  { value: NoticeCategory.MOVE_IN_GUIDE, label: "입주 안내" },
  { value: NoticeCategory.FACILITY_GUIDE, label: "시설 안내" },
  { value: NoticeCategory.EVENTS_BENEFITS, label: "행사 혜택" },
  { value: NoticeCategory.SURVEY, label: "설문 조사" },
  { value: NoticeCategory.ETC, label: "기타" },
];

// url query parameter 타입
type QueryParams = {
  page?: number;
  size?: number;
  sort?: Sort;
  searchType?: string;
  keyword?: string;
  search001?: string;
  id?: string;
  containsTitle?: string;
  isDisplayed?: string;
  category?: string;
  displayStartDate?: string;
  displayEndDate?: string;
  containsTypes?: string;
  partnerId?: string;
  partnerName?: string;
};

/**
 * 공지사항 > 목록 화면
 */
const NoticeList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthority } = usePartnerAuthority();
  const queryParams: QueryParams = useMemo(() => {
    const _queryParams: QueryParams = qsParse(location.search);

    // page, size, sort 없이 최초 진입했을때 default 값 바인딩
    if (!_queryParams?.page) {
      _queryParams.page = 0;
    }
    if (!_queryParams?.size) {
      _queryParams.size = 20;
    }
    if (!_queryParams?.sort) {
      _queryParams.sort = {
        orders: [{ property: "id", direction: "DESC" }],
      };
    }

    // qs.parse에 decoder때문에 검색값 입력 검색시 한번더 decoding
    if (_queryParams.keyword) {
      _queryParams.keyword = decodeURIComponent(_queryParams.keyword);
    }

    return _queryParams;
  }, [location]);

  const [params, setParams] = useState<QueryParams>({ ...queryParams });
  const [pageMeta, setPageMeta] = useState<PageMeta>();
  const [noticeList, setNoticeList] = useState<Array<NoticeModel>>(); // table column에 맞게 가공한 목록
  const [alertModal, setAlertModal] = useState<Modal>({ isOpen: false });
  const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([null, null]);
  const { executeAsync: getNoticeList } = useApiOperation(getNoticeListAsync);
  const { executeAsync: getContractList } = useApiOperation(getContractApply);
  const { executeAsync: getBuildingList } = useApiOperation(getBuildingsAsync);
  // 쿼리파람과 함께 navigate
  const navigateWithQueryParams = useCallback(
    (passParams?: QueryParams, type?: "search" | "pagination") => {
      let data;
      if (type) {
        type === "search" ? (data = { ...params }) : (data = { ...queryParams });
      }

      const newQueryParams = { ...data, ...(passParams || {}) };
      const newQueryParamStr = qs.stringify(newQueryParams, { allowDots: true });
      navigate(location.pathname + "?" + decodeURIComponent(newQueryParamStr));
    },
    [navigate, location.pathname, params, queryParams],
  );

  // 공개기간 검색
  const handleOnDateRangeChange = (range: [Date | null, Date | null]) => {
    const [startDate, endDate] = range;
    setDateRange(range);

    // 날짜 선택값 없이 검색시 날짜 초기화
    if (range[0] === null && range[1] === null) {
      navigateWithQueryParams({ ...params, displayStartDate: undefined, displayEndDate: undefined, page: 0 }, "search");
      return;
    }

    if (startDate && endDate) {
      navigateWithQueryParams(
        {
          ...params,
          displayStartDate: moment(startDate).startOf("days").format(YmdFormat.WITH_TIME_ZONE),
          displayEndDate: moment(endDate).endOf("days").format(YmdFormat.WITH_TIME_ZONE),
          page: 0,
        },
        "search",
      );
    }
  };

  useEffect(() => {
    setParams({ ...queryParams }); // input 초기화
    setDateRange([
      queryParams.displayStartDate ? moment(queryParams.displayStartDate).toDate() : null,
      queryParams.displayEndDate ? moment(queryParams.displayEndDate).toDate() : null,
    ]);

    const fetchApi = async () => {
      // 이전 검색 옵션의 파라미터가 URL 에 남아 함께 전송되지 않도록 제거 후 현재 옵션만 세팅
      const { searchType, keyword, partnerId, partnerName, ...restParams } = queryParams;
      const searchQueryParams = { ...restParams } as Record<string, unknown>;
      searchParamKeys.forEach((key) => delete searchQueryParams[key]);
      searchQueryParams[searchType || "search001"] = keyword || "";

      const noticeParam = Object.fromEntries(
        Object.entries(searchQueryParams).filter(([_, value]) => value !== null && value !== undefined && value !== ""),
      ) as NoticeListParams;

      // 공지사항 목록 조회
      const response = await getNoticeList(noticeParam);
      const noticeList = response.data.data.rows;
      let tempAllContracts: any = [];
      let tempAllBuildings: any = [];
      noticeList.forEach((notice) => {
        if (notice.contracts) {
          tempAllContracts = [...tempAllContracts, ...notice.contracts];
        }
        if (notice.buildings) {
          tempAllBuildings = [...tempAllBuildings, ...notice.buildings];
        }
      });
      const allContracts = [...new Set(tempAllContracts.map((item: any) => item.contractId))];
      const allBuildings = [...new Set(tempAllBuildings.map((item: any) => item.buildingId))];
      let dataContractList: any = [];
      let dataBuildingList: any = [];

      if (allContracts.length > 0) {
        dataContractList = await getContractList({ page: 0, size: 99, contractIds: allContracts.join(",") })
          .then(({ data: contractList }) => contractList.data.content)
          .catch(() => []);
      }

      if (allBuildings.length > 0) {
        dataBuildingList = await getBuildingList({ page: 0, size: 99, id: allBuildings.join(",") })
          .then(({ data: buildingList }) => buildingList.data.content)
          .catch(() => []);
      }

      //신청계약 계약명(없으면 상품명), 건물명 추가
      const newNoticeList = noticeList.map((notice, length) => {
        const contractProductNames = dataContractList
          ?.filter((contract: any) => notice.contracts?.map((item) => item.contractId)?.includes(Number(contract.contractId)))
          .map((contract: any) => contract.contractName || contract.spaceProductName);
        const buildingNames = dataBuildingList
          ?.filter((building: any) => notice.buildings?.map((item) => item.buildingId)?.includes(Number(building.id)))
          .map((building: any) => building.buildingName);
        const newNotice = { ...notice, contractProductNames, buildingNames };
        return newNotice;
      });

      setNoticeList(newNoticeList);
      setPageMeta(response.data.meta.pageMeta);
    };

    fetchApi();
  }, [getNoticeList, queryParams, getContractList, getBuildingList, location.search]);
  return (
    <div className="">
      <div className="contents-container__table">
        <div className="contents-container__search-wrap">
          <div className="left-area">
            {/* section 태그가 지정검색, 조건검색, 영역이고 반드시 필요함 */}
            <section>
              <div className="left-area__index">
                <span>조건검색</span>
              </div>
              <div className="left-area__contents">
                <BaseInputWithSearch
                  type="text"
                  selectValue={params.searchType || ""}
                  inputValue={params.keyword || ""}
                  stateOptions={searchTypes}
                  setStateValue={(searchType: string) => {
                    setParams({ ...params, searchType });
                  }}
                  onChange={(keyword: string) => setParams({ ...params, keyword })}
                  onKeyUp={() => navigateWithQueryParams({ page: 0 }, "search")}
                  onSearchClick={() => navigateWithQueryParams({ page: 0 }, "search")}
                  onClearClick={() => navigateWithQueryParams({ page: 0, keyword: "" }, "search")}
                />
                <div className="minmax160">
                  <BaseSelect
                    placeholder="공개여부"
                    value={params.isDisplayed}
                    className="pre-wrap"
                    stateOptions={isDisplayed}
                    setStateValue={(isDisplayed: CommonFacilityType) => {
                      setParams({ ...params, isDisplayed });
                      navigateWithQueryParams({ ...params, page: 0, isDisplayed }, "search");
                    }}
                  />
                </div>
                <div className="minmax160">
                  <BaseSelect
                    placeholder="카테고리"
                    value={params.category}
                    className="pre-wrap"
                    stateOptions={categoryOptions}
                    setStateValue={(category: string) => {
                      setParams({ ...params, category });
                      navigateWithQueryParams({ ...params, page: 0, category }, "search");
                    }}
                  />
                </div>
                {/* .range-datepicker-input 이 min-width:220px 이므로 래퍼 폭은 220 이상이어야 한다 */}
                <div className="minmax240">
                  <RangeDatepicker
                    dateRange={dateRange}
                    onChange={handleOnDateRangeChange}
                    placeholder="공개기간"
                    onClearClick={() => {
                      setDateRange([null, null]);
                      navigateWithQueryParams({ ...params, displayStartDate: undefined, displayEndDate: undefined, page: 0 }, "search");
                    }}
                  />
                </div>
                <SearchResetButton />
              </div>
            </section>
          </div>
          {isAuthority("w") && (
            <div className="right-area">
              <BaseButton
                title="+ 공지사항 등록"
                onClick={() => {
                  navigate(PagePath.notice.form);
                }}
              />
            </div>
          )}
        </div>

        {noticeList && (
          <BaseTable
            data={noticeList}
            columns={noticeColumns}
            pageIndex={pageMeta?.pageRequest.page || 0}
            totalPages={pageMeta?.totalPages || 0}
            goPage={(page: number) => {
              navigateWithQueryParams({ page }, "pagination");
            }}
            totalElements={Number(pageMeta?.totalElements) || 0}
            orders={queryParams?.sort?.orders}
            disabledSortHeaders={["contractProductNames", "buildingNames"]}
            sizeOption={(number: number) => {
              setParams({ ...params, size: number });
              navigateWithQueryParams({ ...params, page: 0, size: number }, "search");
            }}
            currentSize={Number(params.size)}
            setOrders={(orders?: Array<Order>) => {
              if (orders) {
                navigateWithQueryParams({ ...params, sort: { orders } });
              }
            }}
          />
        )}
      </div>
      {/* 확인버튼만 있는 alert 모달 */}
      {alertModal.isOpen && (
        <BaseModal
          isOpen={true}
          btnRightTitle="확인"
          onClick={() => setAlertModal({ isOpen: false })}
          onClose={() => setAlertModal({ isOpen: false })}
          title={alertModal.title}
        />
      )}
    </div>
  );
};
export default NoticeList;
